import { Zone } from 'app/shared/collections/zones/zone';
import { CesiumZone } from 'app/map/geometry/cesium/CesiumZones/CesiumZone';
import { EntityVisualiser } from 'app/views/render/entity_visualiser';
import { ZoneService } from 'app/shared/zones/zone.service';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { ViewStateService } from 'app/shared/view_state.service';
import { IMSVisualiser } from 'app/views/visualiser';
import { DISPLAY_NOTHING_ENTITY_VISUALISER } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneService';
import { TerrainService } from 'app/map/services/terrain.service';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { CesiumZoneCollection } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneCollection';
import { CesiumZonePositionSampler } from 'app/map/geometry/cesium/CesiumZones/CesiumZonePositionSampler';
import { OffsetManager } from 'app/map/geometry/cesium/OffsetManager';
import { TerrainLoadedStateProvider } from 'app/map/services/terrain_state_provider';

export class CesiumZonePropertyUpdater {
    private _zoneOptionsService: MapOptionsService;
    private _viewStateService: ViewStateService;
    private _currentImsVisualiser: IMSVisualiser;
    private _terrainService: TerrainService;
    private _zoneCollection: ZoneCollection;
    private _cesiumZoneCollection: CesiumZoneCollection;
    private _cesiumZoneStatHeightSampler: CesiumZonePositionSampler;
    private _offsetManager: OffsetManager;

    constructor(
        zoneOptionsService: MapOptionsService,
        viewStateService: ViewStateService,
        terrainService: TerrainService,
        zoneCollection: ZoneCollection,
        cesiumZoneCollection: CesiumZoneCollection,
        cesiumZoneLabelHeightSampler: CesiumZonePositionSampler,
        offsetManager: OffsetManager,
        terrainLoadedStateProvider: TerrainLoadedStateProvider,
    ) {
        this._zoneOptionsService = zoneOptionsService;
        this._viewStateService = viewStateService;
        this._terrainService = terrainService;
        this._zoneCollection = zoneCollection;
        this._cesiumZoneCollection = cesiumZoneCollection;
        this._cesiumZoneStatHeightSampler = cesiumZoneLabelHeightSampler;
        this._offsetManager = offsetManager;

        this._currentImsVisualiser =
            this._viewStateService.currentViewController.getZoneVisualiserForCurrentMapOptions();
        this._zoneOptionsService.addOnMapOptionsChanged(() => {
            // Update ImsVisualiser, we get it using 'getZoneVisualiserForCurrentMapOptions' to ensure it's accurate
            // as it's possible that this listener will fire *before* the listener in the currentViewController
            this._currentImsVisualiser =
                this._viewStateService.currentViewController.getZoneVisualiserForCurrentMapOptions();
        });

        terrainLoadedStateProvider.addTaskToTerrainStateChangeQueue(async () => {
            const cesiumZones = this._cesiumZoneCollection.getAllAsArray();
            for (const zone of cesiumZones) {
                // don't await these updates, let them update asynchronously
                this._updateLabelPosition(zone);
            }
        });
    }

    private _getCurrentEntityVisualiser(): EntityVisualiser {
        if (this._currentImsVisualiser) {
            return this._currentImsVisualiser.entityVisualiser;
        }
        return DISPLAY_NOTHING_ENTITY_VISUALISER;
    }

    public updateZoneLabel(
        imsZone: Zone,
        cesiumZone: CesiumZone,
        time: Cesium.JulianDate,
    ): boolean {
        const newText = this._getCurrentEntityVisualiser().getZoneLabelText(imsZone, time);
        if (cesiumZone.labelText === newText) {
            return false;
        }

        cesiumZone.labelText = newText;
        return true;
    }

    public async updateZonePolygonPositions(
        zone: Zone,
        cesiumZone: CesiumZone,
        time: Cesium.JulianDate,
    ): Promise<void> | null {
        if (!cesiumZone || !zone) {
            return;
        }
        const newPositions = ZoneService.activePolygonCartesian3s(zone, time) || [];
        const actuallyChanged = cesiumZone.changePositionsIfNeeded(newPositions);
        // The label position will need to be updated if the above positions actually changed
        if (actuallyChanged) {
            await this._updateLabelPosition(cesiumZone);
        }
    }

    private async _updateLabelPosition(cesiumZone: CesiumZone) {
        if (this._terrainService.mapTerrainEnabled) {
            cesiumZone.labelPosition =
                await this._cesiumZoneStatHeightSampler.getPositionForLabel3DModeEnabled(
                    cesiumZone,
                );
        } else {
            cesiumZone.labelPosition =
                await this._cesiumZoneStatHeightSampler.getPositionForLabel2DModeEnabled(
                    cesiumZone,
                );
        }
        this._offsetManager.updateByGroupIndex(OffsetManager.ZINDEX.Asset);
    }

    public updateZoneColor(zone: Zone, cesiumZone: CesiumZone, time: Cesium.JulianDate): boolean {
        const newColor = this._getCurrentEntityVisualiser().getZoneColor(zone, time);
        if (cesiumZone.color && cesiumZone.color.equals(newColor)) {
            return false;
        }
        cesiumZone.color = newColor;
        return true;
    }

    /**
     * Updates the show state for both the polygon and label for a given ims Zone and CesiumZone objects,
     * at a particular time
     */
    public updateShowForZone(imsZone: Zone, cesiumZone: CesiumZone, time: Cesium.JulianDate) {
        const newShowPolygon = this._calculatePolygonShownForZone(imsZone, cesiumZone, time);
        const newShowLabel = this._calculateLabelShownForZone(imsZone, cesiumZone, time);

        cesiumZone.showPolygon = newShowPolygon;
        cesiumZone.showLabel = newShowLabel;
    }

    /**
     * Returns whether or not a zone label should be showing at the moment
     */
    private _calculateLabelShownForZone(
        imsZone: Zone,
        cesiumZone: CesiumZone,
        time: Cesium.JulianDate,
    ) {
        return (
            this._getCurrentEntityVisualiser().getZoneLabelShow(imsZone, time) &&
            cesiumZone.hasPositions()
        );
    }

    /**
     * Returns whether or not a zone polygon should be showing for a given time
     */
    private _calculatePolygonShownForZone(
        imsZone: Zone,
        cesiumZone: CesiumZone,
        time: Cesium.JulianDate,
    ) {
        return (
            this._getCurrentEntityVisualiser().getZonePolygonShow(imsZone, time) &&
            cesiumZone.hasPositions()
        );
    }
}
